import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, CreditCard, Star, Shield, Calendar, AlertTriangle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useBillingStore } from '../stores/billingStore';

const Subscribe = () => {
  const navigate = useNavigate();
  const { isAuthenticated, upgradeToPremium } = useAuth();
  const { addPaymentMethod, updateSubscription } = useBillingStore();
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState({
    cardNumber: '',
    expiry: '',
    cvc: '',
    name: ''
  });
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsProcessing(true);

    try {
      // Add payment method
      const paymentMethodId = addPaymentMethod({
        type: 'card',
        lastFour: paymentDetails.cardNumber.slice(-4),
        expiryMonth: paymentDetails.expiry.split('/')[0],
        expiryYear: paymentDetails.expiry.split('/')[1],
        brand: 'Visa', // In a real app, this would be determined by the card number
        isDefault: true
      });

      // Create subscription
      const startDate = new Date();
      const endDate = new Date();
      endDate.setMonth(endDate.getMonth() + 1);

      updateSubscription({
        id: Date.now().toString(),
        userId: 'user-1', // In a real app, this would be the actual user ID
        status: 'active',
        plan: 'premium',
        startDate: startDate.toISOString(),
        currentPeriodEnd: endDate.toISOString(),
        cancelAtPeriodEnd: false,
        price: 19,
        paymentMethodId
      });
      
      upgradeToPremium();
      navigate('/billing');
    } catch (err) {
      setError('Payment failed. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const features = [
    'Unlimited access to all listings',
    'Direct messaging with property owners',
    'Advanced search filters',
    'Save favorite properties',
    'Email notifications for new listings',
    'Virtual tour access'
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <Star className="h-12 w-12 text-yellow-400 fill-current mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            Upgrade to Premium
          </h1>
          <p className="text-xl text-gray-600">
            Get unlimited access to exclusive listings and premium features
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="grid md:grid-cols-2">
            {/* Features Panel */}
            <div className="bg-gray-50 p-8">
              <div className="mb-8">
                <div className="flex items-baseline mb-4">
                  <span className="text-4xl font-bold text-gray-900">$19</span>
                  <span className="text-gray-600 ml-1">/month</span>
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <Calendar className="h-4 w-4 mr-2" />
                  <span>Cancel anytime</span>
                </div>
              </div>

              <div className="space-y-4">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-start">
                    <div className="flex-shrink-0">
                      <Shield className="h-5 w-5 text-green-500" />
                    </div>
                    <p className="ml-3 text-gray-600">{feature}</p>
                  </div>
                ))}
              </div>

              <div className="mt-8 p-4 bg-indigo-50 rounded-lg">
                <div className="flex items-center text-indigo-700">
                  <Shield className="h-5 w-5 mr-2" />
                  <span className="font-medium">30-day money-back guarantee</span>
                </div>
                <p className="mt-2 text-sm text-indigo-600">
                  Not satisfied? Get a full refund within the first 30 days.
                </p>
              </div>
            </div>

            {/* Payment Form */}
            <div className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md">
                    {error}
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Card holder name
                  </label>
                  <input
                    type="text"
                    required
                    value={paymentDetails.name}
                    onChange={(e) => setPaymentDetails({ ...paymentDetails, name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Card number
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      required
                      maxLength={19}
                      value={paymentDetails.cardNumber}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\s/g, '').replace(/(\d{4})/g, '$1 ').trim();
                        setPaymentDetails({ ...paymentDetails, cardNumber: value });
                      }}
                      className="w-full pl-4 pr-10 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                    />
                    <CreditCard className="absolute right-3 top-2.5 h-5 w-5 text-gray-400" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Expiry date
                    </label>
                    <input
                      type="text"
                      required
                      placeholder="MM/YY"
                      maxLength={5}
                      value={paymentDetails.expiry}
                      onChange={(e) => {
                        let value = e.target.value.replace(/\D/g, '');
                        if (value.length >= 2) {
                          value = value.slice(0, 2) + '/' + value.slice(2);
                        }
                        setPaymentDetails({ ...paymentDetails, expiry: value });
                      }}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      CVC
                    </label>
                    <input
                      type="text"
                      required
                      maxLength={3}
                      value={paymentDetails.cvc}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '');
                        setPaymentDetails({ ...paymentDetails, cvc: value });
                      }}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isProcessing}
                  className={`w-full flex items-center justify-center space-x-2 bg-indigo-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-indigo-700 transition-colors ${
                    isProcessing ? 'opacity-75 cursor-not-allowed' : ''
                  }`}
                >
                  <Lock className="h-5 w-5" />
                  <span>{isProcessing ? 'Processing...' : 'Start Premium Subscription'}</span>
                </button>

                <p className="text-sm text-gray-500 text-center">
                  By subscribing, you agree to our{' '}
                  <a href="#" className="text-indigo-600 hover:text-indigo-500">
                    Terms of Service
                  </a>{' '}
                  and{' '}
                  <a href="#" className="text-indigo-600 hover:text-indigo-500">
                    Privacy Policy
                  </a>
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Subscribe;